/** Radius of the shaded area drawn in place of the facility pin. */
export const APPROX_RADIUS_M = 2000;

/** Furthest the drawn centre is moved off the true facility. Kept well inside APPROX_RADIUS_M so
 *  the facility stays comfortably within the shaded area rather than drifting toward its edge. */
export const APPROX_MAX_OFFSET_M = 800;

/** Zoom cap while the shaded area is on screen, so it can't be zoomed down to a single building. */
export const APPROX_MAX_ZOOM = 14;

const EARTH_RADIUS_M = 6371000;
// Derived from the radius rather than the usual 111320 literal so that offsetMeters and
// distanceMeters agree on the size of the Earth — otherwise they disagree by ~0.1%, which is
// harmless on a map but makes any round-trip between them fail to close.
const METERS_PER_DEGREE_LAT = (EARTH_RADIUS_M * Math.PI) / 180;

const toRadians = (degrees) => (degrees * Math.PI) / 180;

// cos(lat) shrinks toward the poles but never reaches 0 in floating point (~6e-17 at 90), so a
// truthiness guard on it would never fire and an east shift up there would divide by near-zero and
// land some absurd number of degrees away. Clamping saturates that instead.
const MIN_COS_LAT = Math.cos(toRadians(89));

/**
 * Shift a point by a distance in metres.
 *
 * A degree of longitude shrinks with latitude (~0.77x at our latitudes), so the east component is
 * scaled by cos(lat). Skipping that squashes the shift east-west and leaves anything positioned
 * this way visibly off where it should be.
 *
 * @param {{lat: number, lng: number}} [location]
 * @param {{north?: number, east?: number}} [shift] metres; negative values go south / west
 * @returns {{lat: number, lng: number}|null} null when the input has no usable geocode
 */
export const offsetMeters = (location, { north = 0, east = 0 } = {}) => {
    const { lat, lng } = location || {};
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    const lngMetersPerDegree = METERS_PER_DEGREE_LAT * Math.max(Math.abs(Math.cos(toRadians(lat))), MIN_COS_LAT);

    return {
        lat: lat + north / METERS_PER_DEGREE_LAT,
        lng: lng + east / lngMetersPerDegree,
    };
};

/**
 * A point near, but not on, the final-mile facility — the centre of the shaded circle shown in
 * place of an exact pin (ENG-9599).
 *
 * Deliberately client-side and re-rolled per page load. The exact facility coordinates still reach
 * the browser in the tracking link's GraphQL payload, so this obscures the pin, not the data.
 *
 * @param {{lat: number, lng: number}} [location] true facility coordinates
 * @returns {{lat: number, lng: number}|null} offset centre, or null if the facility has no geocode
 */
export const genApproximateCenter = (location) => {
    const angle = Math.random() * 2 * Math.PI;
    // sqrt keeps the draw uniform across the disc. Without it points bunch toward the middle, which
    // pulls the drawn centre back onto the facility it is meant to move off.
    const distance = APPROX_MAX_OFFSET_M * Math.sqrt(Math.random());

    return offsetMeters(location, {
        north: distance * Math.sin(angle),
        east: distance * Math.cos(angle),
    });
};

/**
 * Great-circle distance in metres. Used to decide whether the truck is inside the shaded area.
 *
 * @returns {number} metres, or Infinity when either point is missing a geocode
 */
export const distanceMeters = (a, b) => {
    if (!Number.isFinite(a?.lat) || !Number.isFinite(a?.lng)) return Infinity;
    if (!Number.isFinite(b?.lat) || !Number.isFinite(b?.lng)) return Infinity;

    const dLat = toRadians(b.lat - a.lat);
    const dLng = toRadians(b.lng - a.lng);

    const h =
        Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(a.lat)) * Math.cos(toRadians(b.lat)) * Math.sin(dLng / 2) ** 2;

    return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
};

/**
 * Decide how the facility is drawn on the tracking map (ENG-9599).
 *
 * The truck is always drawn at its real coordinates, inside the shaded area as well as outside it.
 * An earlier revision parked the marker on the circle while the truck sat at the facility; product
 * asked for that off, so live tracking reads the same whether or not the feature is on.
 *
 * The circle itself stays up past `pickupComplete` for as long as the truck is still inside it.
 * That status turns true the moment the driver marks the pickup stop complete, which routinely
 * happens on the yard before the truck moves, so lifting the shading on the status alone would
 * take it off a truck that has not left yet.
 *
 * @param {object} args
 * @param {{lat: number, lng: number}|null} [args.approxCenter] drawn circle centre; null when the
 *   feature is off or the facility has no geocode, in which case nothing here applies
 * @param {boolean} [args.pickupComplete] order has left the pre-transit statuses
 * @param {{lat: number, lng: number}} [args.location] the truck's true coordinates
 * @returns {{hideFacility: boolean, truckPosition: {lat: number, lng: number}|null}}
 *   `hideFacility` draws the circle in place of the pin and suppresses the directions line;
 *   `truckPosition` is the truck's real position, or null when there is nothing to draw
 */
export const selectFacilityDisplay = ({ approxCenter, pickupComplete, location } = {}) => {
    const truckPosition =
        Number.isFinite(location?.lat) && Number.isFinite(location?.lng)
            ? { lat: location.lat, lng: location.lng }
            : null;

    if (!approxCenter) {
        return { hideFacility: false, truckPosition };
    }

    // distanceMeters is Infinity when the truck has no coordinates yet, so an unlocated truck is
    // never treated as being at the facility.
    const truckAtFacility = distanceMeters(truckPosition, approxCenter) < APPROX_RADIUS_M;

    return {
        hideFacility: !pickupComplete || truckAtFacility,
        truckPosition,
    };
};

export const getStopCompletionTimes = (orderKey, route) => {
    let pickupStop = (route.stopsByRouteId || []).filter((stop) => {
        return stop.orders?.includes(orderKey) && stop.type === 'PICKUP';
    });

    let dropOffStop = (route.stopsByRouteId || []).filter((stop) => {
        return stop.orders?.includes(orderKey) && stop.type === 'DROPOFF';
    });

    const stopPickupTime =
        pickupStop.length > 0 && pickupStop[0].stop_completion_time
            ? new Date(pickupStop[0].stop_completion_time)
            : null;
    const stopDeliveryTime =
        dropOffStop.length > 0 && dropOffStop[0].stop_completion_time
            ? new Date(dropOffStop[0].stop_completion_time)
            : null;

    return { stopPickupTime, stopDeliveryTime };
};

/**
 * This order's own review, out of the ratings fetched for the whole route.
 *
 * QUERY_BY_ROUTE selects `ratings(where: { order_id: { _in: $orders } })` for every order on the
 * route, unordered, so `results[0]` can belong to another stop's customer. Taking it unfiltered
 * checkmarks Review Submitted for someone who never reviewed, labels the button 'Edit Carrier
 * Review', and hands CarrierReviewModal a foreign `rating_id` — which it spreads into an upsert on
 * `ratings_pkey`, overwriting that other customer's review.
 *
 * @param {object[]} [results] rating rows, each carrying `order_id`
 * @param {string} [orderId] the order being tracked
 * @returns {object|undefined} the matching rating, if there is one
 */
export const selectOrderReview = (results, orderId) => {
    if (!orderId) return undefined;

    return (results || []).find((rating) => rating?.order_id === orderId);
};

/**
 * Format a tracker step's timestamp for display, or blanks when there isn't one.
 *
 * A step can be legitimately complete with no timestamp — an inferred event omits its generated
 * milestone dates (onward-hasura docs/EVENT_TRANSITIONS.md) — so a missing or unparseable value
 * renders as empty rather than 'Invalid Date'.
 *
 * @param {string|Date|null} value
 * @returns {{date: string, time: string}}
 */
export const formatStamp = (value) => {
    if (!value) return { date: '', time: '' };

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return { date: '', time: '' };

    return {
        date: parsed.toDateString(),
        // Hours and minutes only. Slicing the seconds out of a formatted string is what produced
        // '3:00:PM' for single-digit hours, since the offsets only lined up for a 24-hour locale.
        time: parsed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
    };
};
